import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { feeCategoryAPI, feePaymentAPI, studentAPI, teacherAPI } from '../utils/api';
import '../styles/dashboard.css';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatClassLabel = (value) => {
  const normalized = String(value ?? '').trim();
  return /^\d+$/.test(normalized) ? `Class ${normalized}` : normalized;
};

const formatBirthdayDate = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
  });
};

const formatBirthdayCountdown = (days) => {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalBoys: 0,
    totalGirls: 0,
    totalTeachers: 0,
    studentsByClass: [],
    upcomingBirthdays: [],
    lastMonthCollection: 0,
    lastMonthPayments: 0,
    lastMonthStudentsCovered: 0,
  });
  const [defaultFeeCategoryName, setDefaultFeeCategoryName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const today = new Date();
      const lastMonth = new Date();
      const nextSevenDays = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      nextSevenDays.setDate(nextSevenDays.getDate() + 7);

      const formatDate = (date) => date.toISOString().split('T')[0];
      const feeCategoryResponse = await feeCategoryAPI.getAll({ includeDisabled: 1 });
      const feeCategories = feeCategoryResponse.data.data.feeCategories || [];
      const defaultFeeCategory = feeCategories.find((item) => Number(item.isDefault) === 1);
      const feeSummaryParams = {
        fromDate: formatDate(lastMonth),
        toDate: formatDate(today),
        feeCategoryId: defaultFeeCategory ? defaultFeeCategory.id : undefined,
      };

      if (!defaultFeeCategory) {
        delete feeSummaryParams.feeCategoryId;
      }

      const [studentStats, teacherRes, feeSummaryRes, birthdayRes] = await Promise.all([
        studentAPI.getStatistics(),
        teacherAPI.getAll({ limit: 100 }),
        feePaymentAPI.getSummary(feeSummaryParams),
        studentAPI.getUpcomingBirthdays({
          fromDate: formatDate(today),
          toDate: formatDate(nextSevenDays),
        }),
      ]);

      const feeSummary = feeSummaryRes.data.data.summary || {};
      const upcomingBirthdays = birthdayRes.data.data.birthdays || [];

      setDefaultFeeCategoryName(defaultFeeCategory?.name || '');

      setStats({
        totalStudents: studentStats.data.data.totalStudents || 0,
        totalBoys: studentStats.data.data.totalBoys || 0,
        totalGirls: studentStats.data.data.totalGirls || 0,
        totalTeachers: teacherRes.data.data.total || 0,
        studentsByClass: studentStats.data.data.studentsByClass || [],
        upcomingBirthdays,
        lastMonthCollection: feeSummary.totalAmount || 0,
        lastMonthPayments: feeSummary.totalPayments || 0,
        lastMonthStudentsCovered: feeSummary.uniqueStudents || 0,
      });
    } catch (error) {
      console.error('Error fetching statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="spinner"></div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dashboard">
        <h1>Dashboard</h1>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-user-graduate"></i>
            </div>
            <div className="stat-content">
              <h3>Total Students</h3>
              <p className="stat-number">{stats.totalStudents}
                <span className="stat-split-item stat-split-boys mx-3">
                  <i className="fas fa-mars" aria-hidden="true"></i> {stats.totalBoys}
                </span>
                <span className="stat-split-item stat-split-girls">
                  <i className="fas fa-venus" aria-hidden="true"></i> {stats.totalGirls}
                </span>
              </p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-chalkboard-user"></i>
            </div>
            <div className="stat-content">
              <h3>Total Teachers</h3>
              <p className="stat-number">{stats.totalTeachers}</p>
            </div>
          </div>
        </div>

        <div className="section-header">
          <h2>
            Last One Month Fee Summary
            <small className="text-muted ms-2">{defaultFeeCategoryName ? `(${defaultFeeCategoryName})` : ''}</small>
          </h2>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-money-bill-wave"></i>
            </div>
            <div className="stat-content">
              <h3>Total Collection</h3>
              <p className="stat-number stat-number-money">{formatCurrency(stats.lastMonthCollection)}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-receipt"></i>
            </div>
            <div className="stat-content">
              <h3>Total Payments</h3>
              <p className="stat-number">{stats.lastMonthPayments}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-content">
              <h3>Students Covered</h3>
              <p className="stat-number">{stats.lastMonthStudentsCovered}</p>
            </div>
          </div>
        </div>

        <div className="class-breakdown-card card">
          <div className="card-header">
            <div>
              <h3 className="card-title">Students by Class</h3>
              <p className="class-breakdown-subtitle">Compact class-wise student count.</p>
            </div>
          </div>

          {stats.studentsByClass.length > 0 ? (
            <div className="class-breakdown-list">
              {stats.studentsByClass.map((item) => {
                const count = Number(item.count || 0);

                return (
                  <div className="class-breakdown-row" key={item._id}>
                    <div className="class-breakdown-meta">
                      <h4>{formatClassLabel(item._id)}</h4>
                      <span className="class-breakdown-count">{count}</span>
                    </div>
                    <div className="class-breakdown-gender">
                      <span className="class-gender-chip class-gender-boys">
                        <i className="fas fa-mars" aria-hidden="true"></i> {Number(item.boys || 0)}
                      </span>
                      <span className="class-gender-chip class-gender-girls">
                        <i className="fas fa-venus" aria-hidden="true"></i> {Number(item.girls || 0)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="alert alert-info">No class-wise student data available yet.</div>
          )}
        </div>

        <div className="birthday-card card">
          <div className="card-header">
            <div>
              <h3 className="card-title">Upcoming Student Birthdays</h3>
              <p className="class-breakdown-subtitle">Nearest birthdays in next 7 days for active students.</p>
            </div>
          </div>

          {stats.upcomingBirthdays.length > 0 ? (
            <div className="birthday-list">
              {stats.upcomingBirthdays.map((student) => (
                <div className="birthday-row" key={student.id}>
                  <div className="birthday-student">
                    <span className="birthday-avatar">
                      <i className="fas fa-cake-candles" aria-hidden="true"></i>
                    </span>
                    <div>
                      <h4>
                        <Link to={`/students/${student.id}`} className="birthday-student-link">
                          {student.name}
                        </Link>
                      </h4>
                      <p>{formatClassLabel(student.class)}</p>
                    </div>
                  </div>
                  <div className="birthday-meta">
                    <span className="birthday-date">{formatBirthdayDate(student.nextBirthday)}</span>
                    <span className="birthday-countdown">{formatBirthdayCountdown(student.daysUntil)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="alert alert-info">No birthday data available for students yet.</div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
